import { useId, useRef, useState } from 'react';

import type { UploadProgress } from '../lib/api.js';
import { Icon } from './Icon.js';

/**
 * Mirrors the server's accepted list (`ACCEPTED` in
 * `backend/src/modules/scripts/validation/upload-validator.ts`;
 * `script-drop-zone.render.test.tsx` holds the two together). A narrower list
 * only turns away formats the API would take; a wider one invites a refusal
 * after the whole file has been sent.
 */
export const ACCEPTED_SCRIPT_EXTENSIONS: readonly string[] = [
  'fountain', 'txt', 'text', 'md', 'markdown', 'spmd',
  'fdx', 'fadein',
  'pdf',
  'doc', 'docx', 'docm', 'dot', 'dotx', 'odt', 'ott', 'rtf', 'wpd', 'pages', 'sxw', 'html', 'htm',
];

/** The API's own limit (`MAX_BYTES`), which nginx and Fastify enforce too. */
export const MAX_SCRIPT_BYTES = 30 * 1024 * 1024;

export function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;

  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * What can be said about a file BEFORE it is sent: its extension, whether it
 * is empty and whether it fits. Saying it here saves a producer a 30 MB upload
 * to learn the answer. The server still checks all of it, and the content too —
 * this is a courtesy, never the gate.
 */
export function checkScriptFile(file: { name: string; size: number }): string | null {
  const name = file.name.toLowerCase();
  const extension = name.includes('.') ? (name.split('.').pop() ?? '') : '';

  if (!ACCEPTED_SCRIPT_EXTENSIONS.includes(extension)) {
    return `“${file.name}” is not a format we can read. Use a PDF, Final Draft, Fountain, Word or plain-text file — or paste the text instead.`;
  }
  if (file.size === 0) return `“${file.name}” is empty.`;
  if (file.size > MAX_SCRIPT_BYTES) {
    return `“${file.name}” is ${formatFileSize(file.size)}. Screenplays are limited to 30 MB.`;
  }

  return null;
}

/**
 * ONE PLACE A SCREENPLAY IS DROPPED OR CHOSEN.
 *
 * A drop target the size of the gesture people use, with a real "Choose file"
 * button inside it. The button is a `<label>` for a file input that is hidden
 * visually but not removed: the input is the one tab stop, a keyboard and a
 * screen reader reach it as "Choose file", and its focus ring is drawn on the
 * label (`.sr-only:focus-visible + .script-drop-choose`).
 *
 * While a file is on its way the zone becomes that file's row — name, a real
 * progress bar from the upload's own byte count, and Cancel — so there is never
 * a second place on the page saying the same thing. Once the bytes are sent the
 * server still checks and stores the file; that wait has no fraction to report
 * and shows a sweep instead of a bar stuck at 100%.
 *
 * What happens after a file LANDS is the caller's: this component never shows
 * a finished file.
 */
export function ScriptDropZone({
  busy,
  file,
  progress,
  onFile,
  onCancel,
}: {
  /** A file is uploading; drops and picks are refused until it ends. */
  busy: boolean;
  /** The file being uploaded, for its row. */
  file: File | null;
  progress: UploadProgress | null;
  onFile: (file: File) => void;
  onCancel?: () => void;
}) {
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const inputId = `${id}-input`;
  const hintId = `${id}-hint`;
  const problemId = `${id}-problem`;

  const take = (files: FileList | null | undefined) => {
    if (busy || !files || files.length === 0) return;
    if (files.length > 1) {
      setProblem('Add one screenplay at a time.');

      return;
    }

    const picked = files[0]!;
    const found = checkScriptFile(picked);
    setProblem(found);
    if (found === null) onFile(picked);
  };

  if (busy && file !== null) return <UploadRow file={file} progress={progress} onCancel={onCancel} />;

  return (
    <div>
      <div
        className="script-drop"
        data-testid="script-dropzone"
        data-over={over ? '1' : '0'}
        data-invalid={problem === null ? '0' : '1'}
        onDragEnter={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragOver={(e) => {
          // Without preventDefault on BOTH dragover and drop the browser opens
          // the dropped file and the producer loses the page.
          e.preventDefault();
          if (!over) setOver(true);
        }}
        onDragLeave={(e) => {
          // Moving over the icon or the button inside is not leaving.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          take(e.dataTransfer.files);
        }}
        onClick={(e) => {
          // The whole zone opens the picker for a mouse. The label already does
          // that itself; clicking the input twice would open two dialogs.
          if ((e.target as HTMLElement).closest('label, input')) return;
          inputRef.current?.click();
        }}
      >
        <span className="script-drop-icon" aria-hidden="true">
          <Icon name="upload" size={22} />
        </span>
        <span className="script-drop-title">Drop your screenplay here</span>
        {/* THE LIMITS ARE THE API'S: Final Draft and Fountain as well as the
            office formats, and its 30 MB body limit. */}
        <span className="script-drop-meta" id={hintId}>
          PDF, Final Draft, Fountain, Word or plain text · up to 30 MB · scanned PDFs are read too
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="sr-only"
          accept={ACCEPTED_SCRIPT_EXTENSIONS.map((e) => `.${e}`).join(',')}
          data-testid="script-upload"
          disabled={busy}
          aria-describedby={problem === null ? hintId : `${hintId} ${problemId}`}
          aria-invalid={problem === null ? undefined : true}
          onChange={(e) => {
            take(e.target.files);
            // Cleared so choosing the same file again, after fixing it, fires again.
            e.target.value = '';
          }}
        />
        <label htmlFor={inputId} className="btn btn-ghost script-drop-choose" data-testid="script-choose-file">
          Choose file
        </label>
      </div>

      {problem === null ? null : (
        <p className="intake-error" id={problemId} role="alert" data-testid="script-drop-error">
          {problem}
        </p>
      )}
    </div>
  );
}

function UploadRow({
  file,
  progress,
  onCancel,
}: {
  file: File;
  progress: UploadProgress | null;
  onCancel?: () => void;
}) {
  const total = progress !== null && progress.total > 0 ? progress.total : file.size;
  const loaded = Math.min(progress?.loaded ?? 0, total);
  const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
  const sent = percent >= 100;

  return (
    <div className="script-file" data-testid="script-uploading" aria-busy="true">
      <span className="script-file-icon" aria-hidden="true">
        <Icon name="script" size={20} />
      </span>
      <div className="script-file-body">
        <div className="script-file-head">
          <span className="script-file-name">{file.name}</span>
          {onCancel && !sent ? (
            <button type="button" className="ctl" data-testid="script-upload-cancel" onClick={onCancel}>
              Cancel
            </button>
          ) : null}
        </div>

        {sent ? (
          <div className="sa-sweep-track script-file-bar" aria-hidden="true">
            <span className="sa-sweep-bar" />
          </div>
        ) : (
          <div
            className="upload-bar script-file-bar"
            role="progressbar"
            aria-label={`Uploading ${file.name}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            data-testid="script-upload-progress"
          >
            <span style={{ width: `${String(percent)}%` }} />
          </div>
        )}

        <p className="script-file-meta">
          <span role="status">{sent ? 'Checking and saving the file…' : 'Uploading…'}</span>
          {sent ? null : (
            <span aria-hidden="true">{` ${formatFileSize(loaded)} of ${formatFileSize(total)} · ${String(percent)}%`}</span>
          )}
        </p>
      </div>
    </div>
  );
}
